import { combineReducers, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { record } from 'fp-ts';

import { keyBy } from '@mews/fp';

import {
    FETCH_RESERVATION_PRICING_FAILED,
    FETCH_RESERVATION_PRICING_STARTED,
    FETCH_RESERVATION_PRICING_SUCCEEDED,
} from '../entities/reservationPrices/actionTypes';
import { CATEGORIES_VIEW_ERROR_DISSMISED } from '../pricing/actionTypes';
import { FetchServicePricingSucceeded, PricingHashes, ServicePricing } from './types';

const dataInitialState: ServicePricing['data'] = {};
const metaInitialState: ServicePricing['meta'] = {};

const servicePricingDataSlice = createSlice({
    name: 'servicePricingData',
    initialState: dataInitialState,
    reducers: {
        fetchServicePricingSucceeded: (
            state,
            { payload: { categoryPrices, servicePricingHash } }: PayloadAction<FetchServicePricingSucceeded>
        ) => ({
            ...state,
            [servicePricingHash]: {
                ...state[servicePricingHash],
                ...keyBy('categoryId', categoryPrices),
            },
        }),
    },
    extraReducers: (builder) => {
        builder.addCase(CATEGORIES_VIEW_ERROR_DISSMISED, () => dataInitialState);
    },
});

export const { fetchServicePricingSucceeded } = servicePricingDataSlice.actions;

interface FetchServicePricingMetadata {
    servicePricingHash: string;
    pricingHashes: PricingHashes;
}

interface FetchReservationPricingMetadata {
    servicePricingHashes: string[];
}

const servicePricingMetaSlice = createSlice({
    name: 'servicePricingMeta',
    initialState: metaInitialState,
    reducers: {
        fetchServicePricingStarted: (
            state,
            { payload: { servicePricingHash } }: PayloadAction<FetchServicePricingMetadata>
        ) => {
            state[servicePricingHash] = { isFetching: true, hasFailed: false };
        },
        fetchServicePricingFailed: (
            state,
            { payload: { servicePricingHash } }: PayloadAction<FetchServicePricingMetadata>
        ) => {
            state[servicePricingHash] = { isFetching: false, hasFailed: true };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchServicePricingSucceeded, (state, { payload: { servicePricingHash } }) =>
            record.deleteAt(servicePricingHash)(state)
        );
        builder.addCase(
            FETCH_RESERVATION_PRICING_STARTED,
            (
                state,
                {
                    payload: { servicePricingHashes },
                }: PayloadAction<FetchReservationPricingMetadata, typeof FETCH_RESERVATION_PRICING_STARTED>
            ) => {
                servicePricingHashes.forEach((servicePricingHash) => {
                    state[servicePricingHash] = { isFetching: true, hasFailed: false };
                });
            }
        );
        builder.addCase(
            FETCH_RESERVATION_PRICING_FAILED,
            (
                state,
                {
                    payload: { servicePricingHashes },
                }: PayloadAction<FetchReservationPricingMetadata, typeof FETCH_RESERVATION_PRICING_FAILED>
            ) => {
                servicePricingHashes.forEach((servicePricingHash) => {
                    state[servicePricingHash] = { isFetching: false, hasFailed: true };
                });
            }
        );
        builder.addCase(
            FETCH_RESERVATION_PRICING_SUCCEEDED,
            (
                state,
                {
                    payload: { servicePricingHashes },
                }: PayloadAction<FetchReservationPricingMetadata, typeof FETCH_RESERVATION_PRICING_SUCCEEDED>
            ) => {
                servicePricingHashes.forEach((servicePricingHash) => {
                    delete state?.[servicePricingHash];
                });
            }
        );
        builder.addCase(CATEGORIES_VIEW_ERROR_DISSMISED, () => metaInitialState);
    },
});

export const servicePricingReducer = combineReducers({
    data: servicePricingDataSlice.reducer,
    meta: servicePricingMetaSlice.reducer,
});

export const { fetchServicePricingStarted, fetchServicePricingFailed } = servicePricingMetaSlice.actions;
